import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../components/DataTable.js';
import { describeError } from '../../errors.js';
import { useAsyncAction, useFormData } from '../../hooks/index.js';
import type { AdminHttpClient } from '../../http.js';
import type { SocialProviderKind, TenantSocialConfig, TenantSocialProvider, ToastMessage } from '../../types.js';

const PROVIDER_KINDS: SocialProviderKind[] = ['google', 'kakao', 'naver'];
const IMPLEMENTED: SocialProviderKind[] = ['google'];

const EMPTY_FORM = {
  provider: 'google' as SocialProviderKind,
  displayName: 'Google',
  clientId: '',
  redirectUri: '',
  hostedDomain: '',
  allowJit: false,
  allowedDomains: '',
  clientSecret: ''
};

/** Platform-admin social-login config: the single gating entry point (R-1/R-2).
 *  `disabled` (default) means the desktop renders no social buttons. */
export function SocialLoginTab({
  http,
  tenantId,
  onToast
}: {
  http: AdminHttpClient;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<TenantSocialConfig | null>(null);
  const { busy, run } = useAsyncAction(onToast, t);
  const { data: form, setField, reset } = useFormData(() => ({ ...EMPTY_FORM }));

  const reload = useCallback(async () => {
    try {
      setConfig(await http.getSocialConfig(tenantId));
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, t, tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setConfig(null);
      return;
    }
    void reload();
  }, [reload, tenantId]);

  if (!tenantId) return <p className="screen-help">{t('platform.selectTenant')}</p>;

  const mode = config?.socialLoginMode ?? 'disabled';
  const providers = config?.providers ?? [];
  const disabled = mode === 'disabled';

  async function changeMode(next: 'disabled' | 'optional') {
    await run(() => http.updateSocialMode(tenantId, next), {
      afterSuccess: reload,
      successKey: 'common.changed'
    });
  }

  async function toggleEnabled(p: TenantSocialProvider) {
    await run(() => http.updateSocialProvider(tenantId, p.id, { enabled: !p.enabled }), {
      afterSuccess: reload,
      successKey: 'common.changed'
    });
  }

  async function removeProvider(p: TenantSocialProvider) {
    if (!window.confirm(t('socialLogin.confirmDelete', { name: p.displayName }))) return;
    await run(() => http.deleteSocialProvider(tenantId, p.id), {
      afterSuccess: reload,
      successKey: 'common.changed'
    });
  }

  async function submitNew(event: FormEvent) {
    event.preventDefault();
    const domains = form.allowedDomains
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);
    await run(
      async () => {
        await http.createSocialProvider(tenantId, {
          provider: form.provider,
          displayName: form.displayName.trim(),
          config: {
            ...(form.clientId.trim() ? { clientId: form.clientId.trim() } : {}),
            ...(form.redirectUri.trim() ? { redirectUri: form.redirectUri.trim() } : {}),
            ...(form.hostedDomain.trim() ? { hostedDomain: form.hostedDomain.trim() } : {})
          },
          ...(form.clientSecret ? { clientSecret: form.clientSecret } : {}),
          allowJitProvisioning: form.allowJit,
          allowedEmailDomains: domains
        });
        reset();
      },
      {
        afterSuccess: reload,
        successKey: 'common.changed'
      }
    );
  }

  return (
    <section className="content-stack">
      <section className="panel form-grid">
        <h2>{t('socialLogin.title')}</h2>
        <label>
          {t('socialLogin.modeLabel')}
          <select
            value={mode}
            disabled={busy}
            onChange={(e) => void changeMode(e.target.value as 'disabled' | 'optional')}
          >
            <option value="disabled">{t('socialLogin.modeDisabled')}</option>
            <option value="optional">{t('socialLogin.modeOptional')}</option>
          </select>
        </label>
        <p className="screen-help">{t('socialLogin.modeHelp')}</p>
      </section>

      {/* Provider area is inert while disabled — the gate is the mode, not the rows. */}
      <section className="panel" style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
        <h3>{t('socialLogin.providers')}</h3>
        {disabled && <p className="screen-help">{t('socialLogin.disabledNotice')}</p>}
        <DataTable
          rows={providers}
          columns={[
            {
              key: 'provider',
              header: t('socialLogin.provider'),
              render: (provider) => (
                <>
                  {t(`socialLogin.provider_${provider.provider}`)}
                  {!IMPLEMENTED.includes(provider.provider) && (
                    <span className="badge"> {t('socialLogin.comingSoon')}</span>
                  )}
                </>
              )
            },
            {
              key: 'displayName',
              header: t('socialLogin.displayName'),
              render: (provider) => provider.displayName
            },
            {
              key: 'clientSecret',
              header: t('socialLogin.clientSecret'),
              render: (provider) => (provider.hasSecret ? t('socialLogin.secretSet') : t('socialLogin.secretUnset'))
            },
            {
              key: 'enabled',
              header: t('socialLogin.enabledLabel'),
              render: (provider) => (
                <input
                  type="checkbox"
                  checked={provider.enabled}
                  disabled={busy || !IMPLEMENTED.includes(provider.provider)}
                  onChange={() => void toggleEnabled(provider)}
                />
              )
            },
            {
              key: 'actions',
              header: '',
              render: (provider) => (
                <button type="button" disabled={busy} onClick={() => void removeProvider(provider)}>
                  {t('common.delete')}
                </button>
              )
            }
          ]}
          empty={t('socialLogin.noProviders')}
          getRowKey={(provider) => provider.id}
        />
      </section>

      <section className="panel form-grid" style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
        <h3>{t('socialLogin.addProvider')}</h3>
        <form className="form-grid" onSubmit={(e) => void submitNew(e)}>
          <label>
            {t('socialLogin.provider')}
            <select value={form.provider} onChange={(e) => setField('provider', e.target.value as SocialProviderKind)}>
              {PROVIDER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`socialLogin.provider_${k}`)}
                  {!IMPLEMENTED.includes(k) ? ` (${t('socialLogin.comingSoon')})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('socialLogin.displayName')}
            <input value={form.displayName} onChange={(e) => setField('displayName', e.target.value)} required />
          </label>
          <label>
            {t('socialLogin.clientId')}
            <input value={form.clientId} onChange={(e) => setField('clientId', e.target.value)} autoComplete="off" />
          </label>
          <label>
            {t('socialLogin.redirectUri')}
            <input
              value={form.redirectUri}
              onChange={(e) => setField('redirectUri', e.target.value)}
              autoComplete="off"
            />
          </label>
          <p className="screen-help">{t('socialLogin.redirectUriHelp')}</p>
          <label>
            {t('socialLogin.hostedDomain')}
            <input
              value={form.hostedDomain}
              onChange={(e) => setField('hostedDomain', e.target.value)}
              autoComplete="off"
            />
          </label>
          <label>
            {t('socialLogin.clientSecret')}
            <input
              type="password"
              value={form.clientSecret}
              onChange={(e) => setField('clientSecret', e.target.value)}
              autoComplete="new-password"
            />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={form.allowJit} onChange={(e) => setField('allowJit', e.target.checked)} />
            {t('socialLogin.allowJit')}
          </label>
          <label>
            {t('socialLogin.allowedDomains')}
            <input
              value={form.allowedDomains}
              onChange={(e) => setField('allowedDomains', e.target.value)}
              placeholder="corp.io, example.com"
              autoComplete="off"
            />
          </label>
          <button type="submit" disabled={busy || !form.displayName.trim()}>
            {t('socialLogin.create')}
          </button>
        </form>
      </section>
    </section>
  );
}
